import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import { TbCopy, TbLayersLinked, TbReceipt, TbReceiptRefund } from "react-icons/tb";
import { MdClose } from "react-icons/md";
import moment from "moment";
import intl from "react-intl-universal";
import { formatCurrency } from "../../../Utils/formatters";
import {
  getStatementEndToEndId,
  getStatementOperationId,
  mapStatementTransactionRow,
} from "../../../Utils/mapStatementTransactionRow";
import {
  resolveStatementCategoryIcon,
} from "../BaasConsole/Extrato/baasCdnIcons";
import { canRefundPixRecebimento } from "../Pix/pixStatementFilters";
import {
  getExtractTransactionAmountCents,
  getExtractTypeLabel,
  isExtractCreditTransaction,
} from "./extractFilters";
import { downloadPixReceiptPdf } from "../../../Services/downloadPixReceiptPdf";
import { useToast } from "../../../Hooks/toast";
import { isStatementRefund } from "../../../Utils/buildReceiptFromStatementItem";

const DetailRow = ({ label, value, mono = false }) => (
  <div className="min-w-0">
    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</p>
    <p
      className={`mt-1 text-sm font-semibold text-brand-dark dark:text-gray-100 ${
        mono ? "break-all font-mono text-xs" : ""
      }`}
    >
      {value || "—"}
    </p>
  </div>
);

const CopyableIdRow = ({ label, value, copyLabel }) => {
  if (!value) return null;

  const handleCopy = () => {
    navigator.clipboard?.writeText(String(value));
  };

  return (
    <div className="min-w-0">
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</p>
      <div className="mt-1 flex items-start gap-2">
        <p className="min-w-0 flex-1 break-all font-mono text-xs font-semibold text-brand-dark dark:text-gray-100">
          {value}
        </p>
        <button
          type="button"
          className="inline-flex shrink-0 items-center justify-center rounded p-1 text-[#D3A971] hover:bg-gray-100 hover:opacity-80 dark:hover:bg-[#262626]"
          onClick={handleCopy}
          title={copyLabel}
          aria-label={copyLabel}
        >
          <TbCopy className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

const formatSignedAmount = (item) => {
  const isCredit = isExtractCreditTransaction(item);
  const cents = getExtractTransactionAmountCents(item);
  const formatted = formatCurrency(cents);
  if (isCredit) return `+${formatted}`;
  return `-${formatted}`;
};

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const resolvePaymentId = (item) => {
  const direct =
    item?.payment_id || item?.paymentId || item?.pix_id || item?.pixId || null;
  if (direct) return direct;

  // MD06 / estorno debit: OperationId is ChargebackUuid when payment_id was omitted.
  if (isStatementRefund(item)) {
    const op = getStatementOperationId(item);
    if (op && UUID_RE.test(String(op).trim())) return String(op).trim();
  }
  return null;
};

const resolveBatchId = (item) =>
  item?.batch_id || item?.batchId || null;

const hasReceiptDownload = (item) => {
  if (item?.has_receipt_pdf === true || item?.hasReceiptPdf === true) return true;
  if (resolveBatchId(item) || resolvePaymentId(item)) return true;
  return Boolean(
    item?.receipt_pdf_base64
    || item?.receiptPdfBase64
    || (item?.receipt && typeof item.receipt === "object" && Object.keys(item.receipt).length > 0),
  );
};

export const ExtractTransactionDetailModal = ({
  open,
  onClose,
  item,
  onViewReceipt,
  refundEnabled = true,
  onRefund,
}) => {
  const { addToast } = useToast();
  const history = useHistory();
  const [downloading, setDownloading] = useState(false);

  if (!item) return null;

  const canRefund = canRefundPixRecebimento(item, { refundEnabled });
  const isCredit = isExtractCreditTransaction(item);
  const amountLabel = formatSignedAmount(item);
  const dateTime = item.created_at
    ? moment(item.created_at).format("DD/MM/YYYY [às] HH:mm")
    : "—";
  const { title, subtitle, destino, categoria } = mapStatementTransactionRow(item);
  const categoryIcon = resolveStatementCategoryIcon(item?.category?.icon);
  const isBrandPix =
    categoryIcon && /static\.valut\.app\/balance-icons\/pix\.svg/i.test(categoryIcon);
  const isValutBalanceIcon =
    categoryIcon && /static\.valut\.app\/balance-icons\//i.test(categoryIcon);
  const isCircularCdnIcon =
    categoryIcon &&
    !isBrandPix &&
    !isValutBalanceIcon &&
    (/cdn\.valut\.services\/assets\/icons\//i.test(categoryIcon) ||
      /\/cdn\/icons\//i.test(categoryIcon));
  const hasBalances =
    Number.isFinite(Number(item?.balance_before))
    && Number.isFinite(Number(item?.balance_after));
  const receiptAvailable = hasReceiptDownload(item);
  const paymentId = resolvePaymentId(item);
  const batchId = resolveBatchId(item);
  const canDownloadPdf = Boolean(
    item?.has_receipt_pdf === true
    || item?.hasReceiptPdf === true
    || item?.receipt_pdf_base64
    || item?.receiptPdfBase64
    || paymentId
    || batchId,
  );

  const endToEndId = getStatementEndToEndId(item);
  const operationId = getStatementOperationId(item);
  const showOperationId = Boolean(operationId);
  const showEndToEnd = Boolean(endToEndId);

  const handleComprovante = async () => {
    if (canDownloadPdf) {
      setDownloading(true);
      try {
        const result = await downloadPixReceiptPdf({
          receiptPdfBase64: item.receipt_pdf_base64 || item.receiptPdfBase64,
          paymentId,
          batchId,
          accountId: item.account_id || item.accountId,
          fileName: batchId
            ? `comprovante-pix-lote-${batchId}.pdf`
            : `comprovante-pix-${paymentId || operationId || "transacao"}.pdf`,
        });
        if (!result.success) {
          addToast({
            title: "Comprovante indisponível",
            description: result.message || intl.get("extract.receiptUnavailable"),
            type: "error",
          });
        }
      } catch {
        addToast({
          title: "Erro ao baixar comprovante!",
          description: "Ocorreu um erro na operação. Tente novamente mais tarde.",
          type: "error",
        });
      } finally {
        setDownloading(false);
      }
      return;
    }

    // Legacy non-PIX receipts still open the HTML receipt modal.
    onViewReceipt?.(item, item?.is_refund || item?.receipt?.type === "refound" ? "refound" : "receipts");
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle className="!relative !pr-12 !text-base !font-bold !text-brand-dark dark:!text-gray-100">
        {intl.get("extract.transactionDetailTitle")}
        <IconButton
          aria-label={intl.get("words.close")}
          onClick={onClose}
          size="small"
          className="!absolute !right-2 !top-2"
        >
          <MdClose />
        </IconButton>
      </DialogTitle>

      <DialogContent className="!pt-2">
        <div className="space-y-5">
          <div className="flex items-start gap-3">
            {categoryIcon ? (
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-[#262626]">
                <img
                  src={categoryIcon}
                  alt=""
                  className={
                    isCircularCdnIcon
                      ? "h-10 w-10 rounded-full object-cover"
                      : "h-4 w-auto max-w-[18px]"
                  }
                />
              </span>
            ) : null}
            <div className="min-w-0 flex-1">
              <p className="text-base font-semibold text-brand-dark dark:text-gray-100">
                {title || "—"}
              </p>
              {subtitle ? (
                <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
              ) : null}
              <p
                className={`mt-2 text-lg font-bold tabular-nums ${
                  isCredit
                    ? "text-emerald-600 dark:text-emerald-400"
                    : "text-red-600 dark:text-red-400"
                }`}
              >
                {amountLabel}
              </p>
            </div>
          </div>

          <section className="space-y-3">
            <div className="grid gap-4 sm:grid-cols-2">
              <DetailRow label={intl.get("grid.date")} value={dateTime} />
              <DetailRow
                label={intl.get("extract.transactionType")}
                value={getExtractTypeLabel(isCredit ? "credit" : "debit")}
              />
              {destino ? <DetailRow label="Destino" value={destino} /> : null}
              {categoria ? (
                <DetailRow label={intl.get("grid.movement")} value={categoria} />
              ) : null}
            </div>
          </section>

          {hasBalances ? (
            <section className="space-y-3 border-t border-gray-100 pt-4 dark:border-brand-dark-border">
              <div className="grid gap-4 sm:grid-cols-2">
                <DetailRow
                  label={intl.get("extract.balanceBefore")}
                  value={formatCurrency(Math.abs(item.balance_before))}
                />
                <DetailRow
                  label={intl.get("extract.balanceAfter")}
                  value={formatCurrency(Math.abs(item.balance_after))}
                />
              </div>
            </section>
          ) : null}

          {showOperationId || showEndToEnd ? (
            <section className="space-y-3 border-t border-gray-100 pt-4 dark:border-brand-dark-border">
              <div className="grid gap-4">
                <CopyableIdRow
                  label={intl.get("extract.operationId")}
                  value={operationId}
                  copyLabel={intl.get("extract.copyOperationId")}
                />
                <CopyableIdRow
                  label={intl.get("extract.endToEndId")}
                  value={endToEndId}
                  copyLabel={intl.get("extract.copyEndToEndId")}
                />
              </div>
            </section>
          ) : null}

          {!receiptAvailable ? (
            <p className="text-center text-xs text-gray-400 dark:text-gray-500">
              {intl.get("extract.receiptUnavailable")}
            </p>
          ) : null}
        </div>
      </DialogContent>

      <DialogActions className="!flex !flex-wrap !justify-end !gap-2 !border-t !border-gray-100 !px-6 !pb-4 !pt-3 dark:!border-brand-dark-border">
        {batchId ? (
          <button
            type="button"
            className="btn-outline inline-flex items-center gap-2"
            onClick={() => {
              onClose();
              history.push(`/pix/lotes/${encodeURIComponent(batchId)}`);
            }}
          >
            <TbLayersLinked className="h-4 w-4" />
            Acompanhar lote
          </button>
        ) : null}
        {canRefund ? (
          <button
            type="button"
            className="btn-outline inline-flex items-center gap-2"
            onClick={() => onRefund?.(item)}
          >
            <TbReceiptRefund className="h-4 w-4" />
            {intl.get("pix.refund.action")}
          </button>
        ) : null}
        <button
          type="button"
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
          onClick={handleComprovante}
          disabled={!receiptAvailable || downloading}
          title={!receiptAvailable ? intl.get("extract.receiptUnavailable") : undefined}
        >
          <TbReceipt className="h-4 w-4" />
          {downloading ? "Baixando..." : intl.get("extract.viewReceipt")}
        </button>
      </DialogActions>
    </Dialog>
  );
};
